/**
 * Walk a KaTeX-rendered passage DOM and build an HTML string with
 * ``<mark class="digitalSHSATModule__definitionMark">`` wrapping each
 * glossary index's character range.
 *
 * Offsets are character positions into the concatenated text-node
 * content of the root element (matches how the legacy SHSAT module
 * encodes glossary indices — they index against the rendered text,
 * not the source string).
 *
 * Lives in its own file so it can be unit-tested in isolation
 * (offset arithmetic is the most bug-prone bit of the adaptive
 * renderer; tests guard against drift during Phase E follow-ups).
 */

export interface PassageIndex {
  startIndex: number;
  endIndex: number;
  definition: string;
}


export function buildPassageWithMarks(
  rootEl: HTMLElement,
  indices: PassageIndex[],
): string {
  if (indices.length === 0) {
    return rootEl.innerHTML;
  }

  const sorted = [...indices].sort((a, b) => a.startIndex - b.startIndex);

  // Defensive: refuse to silently produce broken HTML on overlapping
  // ranges. Real SHSAT data is non-overlapping (each glossary term is
  // a distinct word), but a data-entry mistake at the admin layer
  // would otherwise produce a malformed nested-mark string.
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startIndex < sorted[i - 1].endIndex) {
      // eslint-disable-next-line no-console
      console.warn(
        `buildPassageWithMarks: overlapping glossary indices ` +
        `[${sorted[i - 1].startIndex}, ${sorted[i - 1].endIndex}) ` +
        `and [${sorted[i].startIndex}, ${sorted[i].endIndex}) — ` +
        `dropping the overlapping entry.`,
      );
      sorted.splice(i, 1);
      i -= 1;
    }
  }

  let html = '';
  let pos = 0;

  const walk = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const outer = el.outerHTML;
      const innerStart = outer.indexOf('>') + 1;
      const innerEnd = outer.lastIndexOf('<');
      html += outer.slice(0, innerStart);
      el.childNodes.forEach(walk);
      html += outer.slice(innerEnd);
      return;
    }
    if (node.nodeType !== Node.TEXT_NODE) return;

    const text = node.textContent ?? '';
    const textStart = pos;
    const textEnd = pos + text.length;

    let cursor = 0;
    sorted.forEach((idx) => {
      const overlapStart = Math.max(idx.startIndex, textStart);
      const overlapEnd = Math.min(idx.endIndex, textEnd);
      if (overlapEnd <= overlapStart) return;
      const localStart = overlapStart - textStart;
      const localEnd = overlapEnd - textStart;
      if (localStart > cursor) {
        html += text.slice(cursor, localStart);
      }
      const escapedDef = escapeAttr(idx.definition);
      html += `<mark class="digitalSHSATModule__definitionMark" data-tooltip="?" data-definition="${escapedDef}">`;
      html += text.slice(localStart, localEnd);
      html += '</mark>';
      cursor = localEnd;
    });
    if (cursor < text.length) {
      html += text.slice(cursor);
    }
    pos = textEnd;
  };

  rootEl.childNodes.forEach(walk);
  return html;
}


export function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
